/**
 * The built-in bed's proportions — a platform on legs, a mattress sunk
 * slightly into it, two crisp pillows + a flat duvet, and a headboard at the
 * head end (-z).
 *
 * The scanned HEIGHT is the top of the made bed, not the headboard: 76 corpus
 * beds have a median height of 29 in and 80% sit between 23 and 31 in, where a
 * headboard stands 45-55 in. So the duvet's top lands on the box top, the
 * mattress, platform and legs share the height below it, and the headboard
 * rises ABOVE the box — measured from retailer beds, not from the scan.
 *
 * Except that a bed drawn to a 29 in scan reads tall: the scan takes in the
 * bedding, and a standard bed's mattress top is 24-25 in. So the mattress top
 * stops at 25 in (the user, 2026-10-30) and a taller scan's duvet sits below
 * its box top; a lower scan keeps its own height.
 *
 * `bedProportions(dims, headboardKind)` is the SINGLE derivation shared by the
 * 3D renderer, the elevation schematic and the staging planner (which seats
 * the kit on the mattress top and hangs the art above the headboard).
 * Floor-relative (floor = 0); the renderer offsets by -height/2.
 *
 * Lives in shared-lib because the planner runs in the cloud function too;
 * `proceduralSpecs.js` re-exports it for the web app.
 */

const IN_TO_M = 0.0254;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Headboard kinds, floor to top, from retailer specs (bedroom staging
 * research doc). Upholstered is what stagers use most; the style packs pick
 * one through `bedding.headboard`.
 */
const BED_HEADBOARDS = {
  // Pottery Barn Clara 49 in, Raleigh 53 in, Hudson 54.5 in.
  panel: { topIn: 52, thickIn: 3.5 },
  // Crate & Barrel Tate 45 in: plain vertical channels.
  channel: { topIn: 45, thickIn: 4 },
  // West Elm Mid-Century 46.75 in: a wood panel with dowelled edges.
  wood: { topIn: 47, thickIn: 1.5 },
};

// Wayfair headboard guide: 14-29 in of a standard headboard shows above the
// mattress. A bed scanned unusually low or high keeps its headboard in range.
const HB_ABOVE_MATTRESS_IN = [14, 29];
// A standard mattress is 10-12 in (Sleep Foundation); the low end, because at
// 12 on the platform the bed read as one deep block (the user, 2026-10-30).
// The platform rail's 5 in is a choice: no source gives one.
const MATTRESS_IN = 10;
const PLATFORM_IN = 5;
// A fitted sheet pulls a mattress's corners and edges round.
const MATTRESS_EDGE_RADIUS_IN = 1.5;
// Floor to mattress top on a standard bed is 24-25 in; never drawn higher.
const MATTRESS_TOP_MAX_IN = 25;

function bedProportions({ width, height, depth }, headboardKind) {
  const hb = BED_HEADBOARDS[headboardKind] || BED_HEADBOARDS.panel;

  const duvetH = 0.05;
  const duvetLift = 0.02; // duvet centre above the mattress top
  const mattressTop = Math.min(
    height - duvetLift - duvetH / 2,
    MATTRESS_TOP_MAX_IN * IN_TO_M
  );
  // A low scan shares its height out instead of sinking the mattress into
  // the floor: at most half of it is mattress, at most 60% of the rest is
  // platform, and the legs take what is left.
  const mattressH = Math.min(MATTRESS_IN * IN_TO_M, mattressTop * 0.5);
  // The mattress RESTS on the platform: its rounded bottom edge leaves a
  // line against the frame. Sunk 8 mm, the two read as one block (review,
  // 2026-10-30); 2 mm only keeps the faces from being coplanar.
  const mattressSink = 0.002;
  const platformTop = mattressTop - mattressH + mattressSink;
  const platformH = Math.min(PLATFORM_IN * IN_TO_M, platformTop * 0.6);
  const legH = platformTop - platformH;

  // The headboard stands INSIDE the scanned box, on the platform's head end:
  // a bed against a wall is scanned with its headboard, and drawn behind the
  // box it stood in the wall (41-47 of 55 corpus beds by more than half an
  // inch). The mattress starts in front of it, half an inch clear, and keeps
  // the old inch at the foot.
  const hbThick = hb.thickIn * IN_TO_M;
  const hbZ = -depth / 2 + hbThick / 2;
  const mattressZ0 = -depth / 2 + hbThick + 0.005;
  const mattressZ1 = depth / 2 - 0.025;
  const mattressD = mattressZ1 - mattressZ0;
  const mattressZ = (mattressZ0 + mattressZ1) / 2;

  const pillowW = clamp(width * 0.42, 0.3, 0.55);
  const pillowH = 0.1;
  const pillowD = 0.3;
  const pillowZ = mattressZ0 + pillowD / 2 + 0.055;
  const pillowSink = 0.006;

  const duvetD = mattressD * 0.62;
  const duvetZ = depth / 2 - duvetD / 2 - 0.02;

  const [aboveMin, aboveMax] = HB_ABOVE_MATTRESS_IN.map((v) => v * IN_TO_M);
  const hbTop = clamp(
    hb.topIn * IN_TO_M,
    mattressTop + aboveMin,
    mattressTop + aboveMax
  );
  const hbH = hbTop - platformTop; // the headboard stands on the platform

  const legInset = 0.06;
  const legR = 0.014;

  return {
    mattressRadius: MATTRESS_EDGE_RADIUS_IN * IN_TO_M,
    legH,
    platformH,
    platformTop,
    mattressH,
    mattressSink,
    mattressTop,
    mattressD,
    mattressZ,
    pillowW,
    pillowH,
    pillowD,
    pillowZ,
    pillowSink,
    duvetH,
    duvetLift,
    duvetD,
    duvetZ,
    hbTop,
    hbH,
    hbThick,
    hbZ,
    legInset,
    legR,
  };
}

module.exports = {
  BED_HEADBOARDS,
  MATTRESS_IN,
  MATTRESS_TOP_MAX_IN,
  bedProportions,
};
